import { BreadCrumbStore } from '@exactsciences/breadcrumb-store'
import { UnleashFeatureFlagWebClient } from '@exactsciences/feature-flag-web-client';
import jwt_decode from 'jwt-decode';
import { registerApplication, start } from 'single-spa';
import { getMfeManifest, loadComponent } from './utils';
import { loadDataDogRum } from './metrics';
import { LoggingWebClient, LoggingWebClientOptions } from '@exactsciences/logging-web-client';
import { PiwikAnalyticsService } from "@exactsciences/analytics-service";
import { MedalliaForm } from "@exactsciences/survey-service";
import { Five9AgentChatService } from "@exactsciences/agent-chat-service";
import { AuthWebClient, MSALAuthWebClient } from '@exactsciences/auth-web-client';
import { from } from "rxjs";
import { initConfig as initLitLocalizeConfigBundles, localizationConfig } from '@helix-design-system/components/dist/components/localization-provider/localization-provider.js';

const styleguide$ = from(import('./styles/styleguide'));
const commonStyles$ = from(import('./styles/common-styles'));
const singleSpaLayout = require('single-spa-layout');

(async () => {
    // enableDebugging();
    const mfeManifest = await getMfeManifest();
    const loadingContainer = await loadComponent('patientHubCoreMfe', './loadingContainerConfig', mfeManifest['patientHubCoreMfe'])();

    initWindowNamespace();
    initConfig(mfeManifest);
    loadDataDogRum();
    loadStyles();
    initLitLocalizeConfig();
    initBreadcrumbStore();
    initFeatureFlagsService();
    initLoggingWebClient();
    initAuthWebClient();
    loadVendorScripts();

    initMFEApplications(mfeManifest, loadingContainer);
    start();
})();

function initWindowNamespace() {
    window.ES_PATIENT_HUB = window.ES_PATIENT_HUB || {};
}

function initConfig(mfeManifest) {
    window.ES_PATIENT_HUB.config = mfeManifest['config'];
}

function initLitLocalizeConfig() {
    window.ES_PATIENT_HUB.litLocalizeConfig = localizationConfig;
    initLitLocalizeConfigBundles(new Map([
        ['es', Promise.all([
            import(`/node_modules/@exactsciences/ph-web-components/dist/generated/locales/es.js`),
            import(`/node_modules/@helix-design-system/components/dist/generated/locales/es.js`),
        ])],
    ]));
}

function initBreadcrumbStore() {
    window.ES_PATIENT_HUB.breadcrumbStore = new BreadCrumbStore();
}

function initFeatureFlagsService() {
    const token = localStorage.getItem('access_token');
    const decodedToken = token && jwt_decode(token);
    const email = decodedToken?.email;
    const unleashFeatureFlagWebClientOptions = window?.ES_PATIENT_HUB?.config?.unleashFeatureFlagWebClientOptions;
    if (!unleashFeatureFlagWebClientOptions) {
        throw new Error("config.unleashFeatureFlagWebClientOptions not defined in manifest");
    }
    if (email) {
        unleashFeatureFlagWebClientOptions.context = {"userId": email};
    }
    const analyticsService = new PiwikAnalyticsService()
    const featureFlagWebClient = new UnleashFeatureFlagWebClient(unleashFeatureFlagWebClientOptions);

    featureFlagWebClient.medalliaParameters = {
        lastFeatureFlagImpression: {}
    };

    featureFlagWebClient.onImpressionHook((event) => {
        analyticsService.logEvent('EXPERIMENT_IMPRESSION', event);
        if (event) {
            featureFlagWebClient.medalliaParameters.lastFeatureFlagImpression[event.featureName] = event;
        }
    });

    window.ES_PATIENT_HUB.featureFlagsWebClient = featureFlagWebClient;
}

function initLoggingWebClient() {
    const loggingWebClientOptions = new LoggingWebClientOptions();
    loggingWebClientOptions.clientId = window['ES_PATIENT_HUB']?.config?.datadog?.clientId;
    loggingWebClientOptions.requestId = window['ES_PATIENT_HUB']?.config?.datadog?.requestId;
    
    const loggingWebClient = new LoggingWebClient(loggingWebClientOptions);
    loggingWebClient.onReady(function () {
        window.ES_PATIENT_HUB.loggingWebClient = loggingWebClient; 
    })
}

function initAuthWebClient() {
   

    
         window['ES_PATIENT_HUB'].featureFlagsWebClient.isEnabledAfterInitialized('patienthub_azure_ad_b2c_toggle')
        .then((isEnabled) => {
            if (isEnabled) {
                const azure = (window)['ES_PATIENT_HUB']?.config?.authentication?.azure;
                const msalauthWebClient = new MSALAuthWebClient({
                    clientId: azure?.clientId,
                    authority: azure?.authority,
                    redirectUri: `${window.location.origin}${azure?.redirectUri}`,
                    postLogoutRedirectUri: `${window.location.origin}/login`,
                    knownAuthorities: azure?.knownAuthorities,
                });

                window.ES_PATIENT_HUB.azureAuthWebClient = msalauthWebClient;
           } else {
            const okta = window["ES_PATIENT_HUB"]?.config?.authentication?.okta;
            const oktaAuthWebClient = new AuthWebClient({
                issuer: okta?.issuer,
                clientId: okta?.clientId,
                signInRedirect: `${window.location.origin}${okta?.redirectUri}`,
                signOutRedirect: `${window.location.origin}/login`,
                authTokenName: "access_token",
                expirationTokenName: "expires_at",
            });
        
            oktaAuthWebClient.enableRefreshToken();
            window.ES_PATIENT_HUB.oktaAuthWebClient = oktaAuthWebClient;
           }
       });
}

function loadVendorScripts() {
    const config = window?.ES_PATIENT_HUB?.config;
    PiwikAnalyticsService.initializePiwikScripts(config['piwik']);
    MedalliaForm.initializeMedalliaScripts(config['medallia']);
    Five9AgentChatService.initializeFive9Scripts(config['five9']);
}

function loadStyles() {
    window.ES_PATIENT_HUB.styles = {
        styleguide$: styleguide$,
        commonStyles$: commonStyles$
    };
    styleguide$.subscribe();
    commonStyles$.subscribe();
}

function initMFEApplications(mfeManifest, loadingContainer) {
    const loadingFns = {};
    [
        'patientHubAccountMfe/activateConfig',
        'patientHubAccountMfe/loginConfig',
        'patientHubAccountMfe/passwordResetConfig',
        'patientHubAccountMfe/profileConfig',
        'patientHubAccountMfe/supportConfig',
        'patientHubAccountMfe/gatedConfig',
        'patientHubCoreMfe/notificationConfig',
        'patientHubCoreMfe/timeoutModalConfig',
        'patientHubCoreMfe/wildcardRedirectConfig',
        'patientHubCoreMfe/topMessageBarSspaApp',
        'patientHubKitreturnMfe/kitReturnApp',
        'patientHubProductMfe/dashboardConfig',
        'patientHubProductMfe/testsConfig'
    ].forEach((appName) => {
        const remoteName = appName.split('/')[0];
        const moduleName = appName.split('/')[1];
        loadingFns[appName] = () => loadComponent(remoteName, './' + moduleName, mfeManifest[remoteName])();
    });

    const propMap = {
        'patientHubAccountMfe/activateConfig': {},
        'patientHubAccountMfe/loginConfig': {},
        'patientHubAccountMfe/passwordResetConfig': {},
        'patientHubAccountMfe/profileConfig': {},
        'patientHubAccountMfe/supportConfig': {},
        'patientHubAccountMfe/gatedConfig': {},
        'patientHubCoreMfe/notificationConfig': {},
        'patientHubCoreMfe/timeoutModalConfig': {},
        'patientHubCoreMfe/wildcardRedirectConfig': {},
        'patientHubCoreMfe/topMessageBarSspaApp': {},
        'patientHubKitreturnMfe/kitReturnApp': {},
        'patientHubProductMfe/dashboardConfig': {},
        'patientHubProductMfe/testsConfig': {}
    };
    const props = Object.assign({}, ...Object.values(propMap));
    const data = {
        props: props,
        loaders: {
            loadingContainer
        }
    };

    const routes = singleSpaLayout.constructRoutes(document.querySelector('#single-spa-layout'), data);
    const applications = singleSpaLayout.constructApplications({
        routes,
        loadApp: ({ name }) => loadingFns[name]()
    });
    const layoutEngine = singleSpaLayout.constructLayoutEngine({ routes, applications });
    applications.forEach((app) => {
        registerApplication({
            ...app,
            customProps: propMap[app.name]
        })
    });
}
